import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet, ScrollView, TextInput } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { SkeletonRow } from "@/components/ui/Skeleton";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { UserType } from "@/types/db/users";
import { UserPayType } from "@/types/db/user_pay";
import { useTheme } from "@/hooks/useTheme";
import usePermissions from "@/hooks/usePermissions";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Menu } from "@/components/Menu";
import ThemedInput from "@/components/ThemedInput";
import { Button } from "@/components/ui/Button";
import { userService } from "@/services/userService";
import useToast from "@/hooks/useToast";

type PayMetaData = {
  type?: "hourly" | "daily" | "salary";
  rate?: number;
  effective_date?: string;
  notes?: string;
  everee_id?: string;
};

export function UserPayTab() {
  const { colors } = useTheme();
  const { entityData, loading } = useEntityEdit<UserType>();
  const { user_pay: payPermissions } = usePermissions();
  const toast = useToast();

  // Local state - independent of parent edit state
  const [payData, setPayData] = useState<UserPayType | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Derive canEdit from permissions
  const canEdit = payPermissions.create || payPermissions.update;

  // Sync local state with entity data
  useEffect(() => {
    if (entityData?.user_pay) {
      setPayData(entityData.user_pay);
    }
  }, [entityData?.user_pay]);

  const metaData: PayMetaData = payData?.meta_data || {};
  const payType = metaData?.type || "";
  const rate = metaData?.rate;
  const effectiveDate = metaData?.effective_date || "";
  const notes = metaData?.notes || "";
  const evereeId = metaData?.everee_id || "";
  const hasData = !!payType;

  // Format rate for display
  const formatRate = (value: number | undefined, type: string): string => {
    if (value === undefined || value === null) return "Not set";
    const formatted = `$${value.toFixed(2)}`;
    if (type === "hourly") return `${formatted}/hr`;
    if (type === "daily") return `${formatted}/day`;
    if (type === "salary") return `${formatted}/year`;
    return formatted;
  };

  // Format date for display
  const formatDate = (dateStr: string): string => {
    if (!dateStr) return "Not set";
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const handleUpdatePayData = useCallback((updates: Partial<PayMetaData>) => {
    setPayData(
      (prev) =>
        ({
          ...prev,
          meta_data: {
            ...prev?.meta_data,
            ...updates,
          },
        }) as UserPayType,
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (!entityData?.id) return;

    setSaving(true);
    try {
      await userService.updateUserPay(entityData.id, {
        meta_data: payData?.meta_data || {},
      });
      toast.showToast({ title: "Saved", message: "Pay information updated", toastType: "success" });
      setIsEditing(false);
    } catch (error: any) {
      toast.showToast({ title: "Error", message: error.message, toastType: "error" });
    } finally {
      setSaving(false);
    }
  }, [entityData?.id, payData, toast]);

  const handleDelete = useCallback(async () => {
    if (!entityData?.id) return;

    setSaving(true);
    try {
      await userService.updateUserPay(entityData.id, {
        meta_data: {},
      });
      setPayData(
        (prev) =>
          ({
            ...prev,
            meta_data: {},
          }) as UserPayType,
      );
      toast.showToast({ title: "Deleted", message: "Pay information cleared", toastType: "success" });
      setIsEditing(false);
    } catch (error: any) {
      toast.showToast({ title: "Error", message: error.message, toastType: "error" });
    } finally {
      setSaving(false);
    }
  }, [entityData?.id, toast]);

  const handleCancel = useCallback(() => {
    // Reset to original data
    if (entityData?.user_pay) {
      setPayData(entityData.user_pay);
    }
    setIsEditing(false);
  }, [entityData?.user_pay]);

  // If no read permission, show access denied
  if (!payPermissions.read) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <MaterialCommunityIcons name="lock" size={48} color={colors.textSecondary} />
        <ThemedText type="defaultSemiBold" style={{ marginTop: 16 }}>
          Access Denied
        </ThemedText>
        <ThemedText style={{ opacity: 0.7, marginTop: 8 }}>
          You don&apos;t have permission to view pay information
        </ThemedText>
      </ScrollView>
    );
  }

  if (loading || !entityData) {
    return (
      <View style={styles.container}>
        <View style={styles.section}>
          <SkeletonRow />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container} pointerEvents={saving ? "none" : "auto"}>
      <ScrollView style={styles.scrollContent} contentContainerStyle={styles.scrollContainer}>
        <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
          {isEditing ? (
            <View style={styles.editForm}>
              {/* Pay Type */}
              <View style={styles.fieldRow}>
                <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Pay Type:</ThemedText>
                <View style={{ flex: 1 }}>
                  <ThemedInput
                    value={payType}
                    onChangeText={(text) => handleUpdatePayData({ type: text as "hourly" | "daily" | "salary" })}
                    items={[{ text: "hourly" }, { text: "daily" }, { text: "salary" }]}
                    placeholder="Select type"
                  />
                </View>
              </View>

              {/* Rate */}
              <View style={styles.fieldRow}>
                <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Rate:</ThemedText>
                <TextInput
                  style={[
                    styles.input,
                    styles.flexInput,
                    { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                  ]}
                  placeholder="0.00"
                  placeholderTextColor={colors.placeholder}
                  value={rate?.toString() || ""}
                  onChangeText={(text) => handleUpdatePayData({ rate: parseFloat(text) || 0 })}
                  keyboardType="decimal-pad"
                />
              </View>

              {/* Effective Date */}
              <View style={styles.fieldRow}>
                <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Effective Date:</ThemedText>
                <TextInput
                  style={[
                    styles.input,
                    styles.flexInput,
                    { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                  ]}
                  placeholder="YYYY-MM-DD"
                  placeholderTextColor={colors.placeholder}
                  value={effectiveDate}
                  onChangeText={(text) => handleUpdatePayData({ effective_date: text })}
                />
              </View>

              {/* Notes */}
              <View style={styles.fieldRow}>
                <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Notes:</ThemedText>
                <TextInput
                  style={[
                    styles.input,
                    styles.flexInput,
                    styles.multilineInput,
                    { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                  ]}
                  placeholder="Optional notes..."
                  placeholderTextColor={colors.placeholder}
                  value={notes}
                  onChangeText={(text) => handleUpdatePayData({ notes: text })}
                  multiline
                />
              </View>

              {/* Everee ID */}
              <View style={styles.fieldRow}>
                <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Everee ID:</ThemedText>
                <TextInput
                  style={[
                    styles.input,
                    styles.flexInput,
                    { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                  ]}
                  placeholder="Everee ID"
                  placeholderTextColor={colors.placeholder}
                  value={evereeId}
                  onChangeText={(text) => handleUpdatePayData({ everee_id: text })}
                />
              </View>

              {/* Actions */}
              <View style={styles.actions}>
                <Button icon="close" style={[styles.iconBtn, { borderColor: colors.border }]} onPress={handleCancel} />
                <Button
                  icon="content-save"
                  style={[styles.iconBtn, styles.saveIconBtn, { backgroundColor: colors.primary }]}
                  textStyle={{ color: "white" }}
                  onPress={handleSave}
                />
              </View>
            </View>
          ) : (
            <View style={styles.itemContent}>
              <View style={styles.itemInfo}>
                {/* Pay Type */}
                <View style={styles.displayRow}>
                  <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Pay Type</ThemedText>
                  <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
                    {payType ? payType.charAt(0).toUpperCase() + payType.slice(1) : "Not set"}
                  </ThemedText>
                </View>

                {/* Rate */}
                <View style={styles.displayRow}>
                  <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Rate</ThemedText>
                  <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
                    {formatRate(rate, payType)}
                  </ThemedText>
                </View>

                {/* Effective Date */}
                <View style={styles.displayRow}>
                  <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Effective Date</ThemedText>
                  <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
                    {formatDate(effectiveDate)}
                  </ThemedText>
                </View>

                {/* Notes */}
                {notes && (
                  <View style={styles.displayRow}>
                    <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Notes</ThemedText>
                    <ThemedText style={{ fontSize: 14, opacity: 0.8 }}>{notes}</ThemedText>
                  </View>
                )}

                {/* Everee ID */}
                {evereeId && (
                  <View style={styles.displayRow}>
                    <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Everee ID</ThemedText>
                    <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
                      {evereeId}
                    </ThemedText>
                  </View>
                )}
              </View>

              {/* Menu for edit/delete actions based on permissions */}
              {(canEdit || payPermissions.delete) && (
                <Menu
                  buttonStyle={{
                    alignSelf: "flex-start",
                    borderWidth: 0,
                    backgroundColor: "transparent",
                  }}
                  hideArchive={true}
                  hideDuplicate={true}
                  onEdit={canEdit ? () => setIsEditing(true) : undefined}
                  onDelete={payPermissions.delete && hasData ? handleDelete : undefined}
                />
              )}
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    flex: 1,
  },
  scrollContainer: {
    padding: 16,
    gap: 12,
  },
  section: {
    padding: 16,
    gap: 16,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  card: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    overflow: "hidden",
  },
  itemContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: 16,
  },
  itemInfo: {
    flex: 1,
    gap: 16,
  },
  editForm: {
    padding: 16,
    gap: 16,
  },
  fieldRow: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
  },
  displayRow: {
    gap: 4,
  },
  input: {
    height: 40,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  flexInput: {
    flex: 1,
  },
  multilineInput: {
    height: 80,
    paddingTop: 10,
    textAlignVertical: "top",
  },
  label: {
    fontSize: 14,
    minWidth: 120,
  },
  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 4,
  },
  iconBtn: {
    height: 40,
    width: 40,
    paddingHorizontal: 0,
    borderRadius: 8,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  saveIconBtn: {
    borderWidth: 0,
  },
});
